/* Account-area auth guard.
 *
 * Plan §"Auth guard behavior for all account routes" + §"Guard timing contract":
 *   - Guard applies to platform `/account/*` (and legacy tenant redirects) BEFORE
 *     rendering protected pages and BEFORE any account data fetch.
 *   - Without a session: park the current path as a platform `/account/…`
 *     return target, then soft-redirect to `/sign-in` via `<Navigate replace />`.
 *   - With a session: render the protected subtree.
 *
 * Ordinary unauthenticated hop uses React Router `<Navigate to="/sign-in" replace />`
 * (B5 soft Navigate). KEEP-hard `window.location.replace` is reserved for
 * purchases park landing and session-ending post-logout landing only.
 *
 * R1 — `oidcLandingPending` uses `window.location.search` as SSOT (not RR
 * `location.search` alone). After `stripOidcLandQueryFromLocation` + no session,
 * pending is false and `redirectToSignIn` is allowed.
 *
 * G19 — session authority is CSP memory session after `/me` **200**.
 * BAN getToken / readable access cookie as signed-in proof.
 * OIDC land pending = land query only (no readable-token gate).
 *
 * F1 — `saveReturnTarget` / platform park also uses pathname + window.search
 * (SSOT) so a stale RR `?oidcLand=1` is never persisted after strip.
 *
 * Render terminals (R1–R6 SoT) — pipeline → R map:
 *   R1 wait — !allowScreenInit && redirectToSignIn !== true (boot / OIDC hold)
 *   R2 children — ready / allowScreenInit with redirectToSignIn !== true
 *   R3 wait — redirectToSignIn + purchases KEEP-hard branch (no Navigate)
 *   R4 wait — redirectToSignIn + session-ending KEEP-hard branch (no Navigate)
 *   R5 null — redirectToSignIn + already on `/sign-in`
 *   R6 Navigate — redirectToSignIn ordinary hop exclusive soft Navigate
 * Invariant: redirectToSignIn never pairs with R2 children (allowScreenInit
 * false whenever redirectToSignIn === true from evaluateIdentityPipeline).
 */

import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { evaluateIdentityPipeline } from '../../../navigation/identityPipeline.js';
import {
  isAccountOrdersPath,
  isAccountShopPurchasesPath,
  resolvePostLogoutLandingPath,
} from '../../shop/resolveShopHref.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useOidcLandGuardPending } from '../../../shared/session/useOidcLandGuardPending.js';
import {
  isSessionEndingLatchActive,
} from '../../../shared/session/customerAuthSync.js';
import { resolveAccountNavTenantCode } from '../../../shared/tenant/customerApiTenant.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import {
  isSelfScanReturnPath,
  savePlatformReturnTarget,
  savePostAuthOrdersIntent,
  saveReturnTarget,
  validatePlatformReturnTarget,
} from '../../../shared/session/returnTarget.js';

interface RequireCustomerAuthProps {
  children: ReactNode;
}

/** Wave D — remap `/{tenant}/account/…` → `/account/…` for park storage. */
function toPlatformAccountParkPath(pathWithQuery: string): string {
  const q = pathWithQuery.indexOf('?');
  const pathname = q === -1 ? pathWithQuery : pathWithQuery.slice(0, q);
  const search = q === -1 ? '' : pathWithQuery.slice(q);
  if (pathname === '/account' || pathname.startsWith('/account/')) {
    return `${pathname}${search}`;
  }
  const match = /^\/[^/]+(\/account(?:\/.*)?)$/.exec(pathname);
  if (match?.[1] !== undefined) {
    return `${match[1]}${search}`;
  }
  return `${pathname}${search}`;
}

/** Orders surface — platform bare path + legacy tenant (Wave D / part-1 detectors). */
function isOrdersParkPath(pathname: string): boolean {
  return (
    isAccountOrdersPath(pathname) ||
    pathname === '/account/orders' ||
    pathname.startsWith('/account/orders/')
  );
}

/** Purchases surface — platform bare path + legacy tenant. */
function isPurchasesParkPath(pathname: string): boolean {
  return (
    isAccountShopPurchasesPath(pathname) ||
    pathname === '/account/shop' ||
    pathname.startsWith('/account/shop/')
  );
}

/** Residual G1 — extract org tenant from Self-Scan path for twin park. */
function tenantCodeFromSelfScanPath(pathname: string): string | null {
  const match = /^\/([^/]+)\/(?:shop\/scanner|self-scan)(?:\/|$)/.exec(pathname);
  const tenant = match?.[1]?.trim() ?? '';
  if (tenant.length === 0 || tenant.startsWith('__')) {
    return null;
  }
  return tenant;
}

export function RequireCustomerAuth({
  children,
}: RequireCustomerAuthProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const { session, sessionHydrated } = useCustomerSession();
  const location = useLocation();
  const { oidcLandingPending, oidcLandBudgetExhausted } =
    useOidcLandGuardPending();
  const windowOidcSearch =
    typeof window !== 'undefined' ? window.location.search : '';

  const pipelineDecision = useMemo(() => {
    return evaluateIdentityPipeline({
      routeMatched: true,
      entitlementsLoading: false,
      entitlementsGranted: true,
      entitlementMode: 'inline-unavailable',
      sessionHydrated,
      sessionPresent: session !== null,
      accountMeReady: true,
      accountMeValid: true,
      oidcLandingPending,
      oidcLandBudgetExhausted,
    });
  }, [
    oidcLandingPending,
    oidcLandBudgetExhausted,
    session,
    sessionHydrated,
  ]);

  useLayoutEffect(() => {
    if (pipelineDecision.redirectToSignIn !== true) {
      return;
    }
    // F1 — window.search SSOT (same as landing pending). RR location.search can
    // still hold stale ?oidcLand=1 after replaceState strip.
    const pathWithQuery = `${location.pathname}${windowOidcSearch}`;
    const homeTenant = resolveAccountNavTenantCode(tenantCode);
    // Unauthenticated Purchases → `/favorites`; logout on `/favorites` → stay `/favorites`
    // (empty OK). Stage 3 `/` → `/browse`; Oblíbené → always `/favorites` via
    // resolveShopHref. Recovery/post-auth no-favs → `/browse` (resolveGuestShopHomeHref).
    // Latch on or off — post-wipe paint must not park + sign-in.
    // KEEP-hard: purchases park landing via window.location.replace.
    if (isPurchasesParkPath(location.pathname)) {
      const landing = resolvePostLogoutLandingPath(location.pathname, homeTenant);
      if (location.pathname !== landing) {
        window.location.replace(landing);
      }
      return;
    }
    // G2 — Orders: park return target before latch early-return (peer/local session-null).
    // Wave D — park platform `/account/orders…` + orders intent.
    const onOrders = isOrdersParkPath(location.pathname);
    const parkPath = toPlatformAccountParkPath(pathWithQuery);
    if (onOrders) {
      savePlatformReturnTarget(parkPath);
      savePostAuthOrdersIntent();
    }
    // Residual G1 — Self-Scan: park same tenant/SP Self-Scan path (not platform hub).
    // Only allowlisted shapes are stored (validatePlatformReturnTarget).
    const onSelfScan = isSelfScanReturnPath(pathWithQuery);
    if (onSelfScan) {
      const selfScanTenant =
        tenantCodeFromSelfScanPath(location.pathname) ?? homeTenant;
      const normalizedSelfScan = validatePlatformReturnTarget(pathWithQuery);
      if (
        normalizedSelfScan !== null &&
        selfScanTenant.length > 0 &&
        !selfScanTenant.startsWith('__')
      ) {
        saveReturnTarget(normalizedSelfScan, {
          tenantCode: selfScanTenant,
          source: 'shop',
        });
      }
    }
    // Intentional logout (latch) on other /account/* — land without parking return target
    // (Orders already parked above). KEEP-hard session-ending landing.
    if (isSessionEndingLatchActive()) {
      const landing = resolvePostLogoutLandingPath(location.pathname, homeTenant);
      if (location.pathname !== landing) {
        window.location.replace(landing);
      }
      return;
    }
    if (!onOrders && !onSelfScan) {
      savePlatformReturnTarget(parkPath);
    }
    // Ordinary hop: park only — soft `<Navigate>` in render (R6). BAN
    // window.location.replace('/sign-in') and imperative navigate here.
  }, [
    pipelineDecision.redirectToSignIn,
    session,
    sessionHydrated,
    tenantCode,
    location.pathname,
    windowOidcSearch,
  ]);

  const waitChrome = (
    <p className="p-4 text-sm text-[var(--color-on-surface-muted)]" role="status">
      {t('account.sessionHydrating')}
    </p>
  );

  // R1 — boot (!sessionHydrated) or OIDC hold: allowScreenInit false, no redirect.
  if (
    !pipelineDecision.allowScreenInit &&
    pipelineDecision.redirectToSignIn !== true
  ) {
    return waitChrome;
  }

  // R2 — ready/allowScreenInit with redirectToSignIn !== true → children.
  // Pipeline invariant: allowScreenInit true never sets redirectToSignIn.
  if (
    pipelineDecision.allowScreenInit &&
    pipelineDecision.redirectToSignIn !== true
  ) {
    return <>{children}</>;
  }

  // redirectToSignIn true, allowScreenInit false — R3–R6 mutex (no double Navigate
  // while KEEP-hard effects run for purchases / session-ending). Never R2.
  if (isPurchasesParkPath(location.pathname)) {
    return waitChrome; // R3
  }
  if (isSessionEndingLatchActive()) {
    return waitChrome; // R4
  }
  if (location.pathname === '/sign-in') {
    return null; // R5 — never wait chrome, never Navigate, never children
  }
  return <Navigate to="/sign-in" replace />; // R6
}
